'use client';

import noop from 'lodash/noop';
import { useTranslations } from 'next-intl';
import { PropsWithChildren, useState } from 'react';

import { trackAnalytics } from '@/analytics/track';
import { EventType } from '@/analytics/types';
import { ProductSelectData } from '@/app/hooks';
import { OrderLineItem } from '@/types/Order';
import type { Product } from '@/types/Product';

import FormActionButton from '../FormActionButton';
import SubmitButton from '../SubmitButton';
import MultiRing from './MultiRing';
import { PaginationProps } from './MultiRing/Pagination';
import SingleRing from './SingleRing';

export interface ProductSelectProps
  extends Partial<
    Pick<
      ProductSelectData,
      'isFirstPage' | 'isMultiRing' | 'isMultiPage' | 'isPageComplete'
    >
  > {
  readonly enableSubmit?: boolean;
  readonly lineItems?: OrderLineItem[];
  readonly paginationProps?: PaginationProps;
  readonly products: Product[];
  readonly submitText: string;
  readonly onPrev?: () => void;
  readonly onSelect: (items: OrderLineItem[]) => void;
  // onSubmit function to handle API call on submit
  readonly onSubmit: () => void;
}

const ProductSelect = ({
  children,
  enableSubmit,
  isFirstPage = true,
  isMultiPage = false,
  isMultiRing = false,
  isPageComplete,
  lineItems = [],
  paginationProps,
  products,
  submitText,
  onPrev = noop,
  onSelect,
  onSubmit,
}: PropsWithChildren<ProductSelectProps>) => {
  const t = useTranslations();

  const [sizeError, setSizeError] = useState<string>('');

  const handleSubmitClick = () => {
    const action = isMultiRing ? 'submit sizes' : 'submit size';
    trackAnalytics(EventType.CTAClicked, {
      cta: submitText,
      action,
      location: 'body',
      path: '/select',
    });

    if (isPageComplete) {
      onSubmit();
    } else {
      setSizeError(t('eop_select_size_error'));
    }
  };

  const handleSelect = (items: ProductSelectProps['lineItems']) => {
    onSelect(items!);
  };

  const styles = {
    wrap: 'flex flex-col gap-12',
  };

  return (
    <div data-testid="product-select" className={styles.wrap}>
      {isMultiRing ? (
        <MultiRing
          isMultiPage={isMultiPage}
          lineItems={lineItems}
          paginationProps={paginationProps}
          products={products}
          onChange={handleSelect}
        />
      ) : (
        <SingleRing
          className={styles.wrap}
          products={products}
          selectedSku={lineItems[0]?.sku}
          onSelect={(sku: OrderLineItem['sku']) => {
            setSizeError('');
            handleSelect([{ ...lineItems[0], sku }]);
          }}
          sizeError={sizeError}
        />
      )}

      {children}

      <div
        data-testid="product-select-actions"
        className="flex items-center justify-center gap-4 md:justify-end"
      >
        {isMultiPage && !isFirstPage ? (
          <FormActionButton
            data-testid="back-button"
            variant="outlined-dark"
            onClick={onPrev}
          >
            {t('back')}
          </FormActionButton>
        ) : null}

        <SubmitButton
          disabled={!(enableSubmit || isPageComplete)}
          onClick={handleSubmitClick}
        >
          {submitText}
        </SubmitButton>
      </div>
    </div>
  );
};

export default ProductSelect;
